import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, Grid, Typography } from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RepeatIcon from '@mui/icons-material/Repeat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InboxIcon from '@mui/icons-material/Inbox';
import PageTitle from '../../common/PageTitle';
import AwbLinkCell from '../../common/AwbLinkCell';
import { pageContainerSx, badgeSx, btnRowSx } from '../../styles/patterns';
import { ERROR, WARNING, SUBTLE } from '../../styles/foundation/colors';
import { formatDate, parseDate } from '../../helpers/UtilityHelper';
import { awbTextSx, getStatusChipSx, primaryTextSx, secondaryTextSx, tertiaryTextSx } from '../orders/list2/cellStyles';
import KpiCard from '../appointments/shared/KpiCard';
import FilterBar from '../appointments/shared/FilterBar';
import PageBanner from '../appointments/shared/PageBanner';
import ExportButton from '../appointments/shared/ExportButton';
import ExpandableAppointmentsTable from '../appointments/shared/ExpandableAppointmentsTable';
import useAppointmentSearch from '../appointments/shared/useAppointmentSearch';
import { getReasonLabel, useAppointmentConfig } from '../appointments/shared/useAppointmentConfig';
import {
  NO_NEW_DATE_AGE_BANDS, NO_NEW_DATE_FILTER, ageBandFilter, ageBandOf, buildOverdueFilter, lastMiss,
} from '../appointments/shared/constants';
import SectionTitle from '../appointments/shared/SectionTitle';
import ShareBar, { PctChip } from '../appointments/shared/ShareBar';
import { TOKEN_COLOR } from '../appointments/shared/tokens';
import { MISS_ATTRIBUTION, NO_FIRST_APPOINTMENT_DAYS, errorText, fromUtc, noFirstAppointmentFilter } from './constants';
import { viewColumn } from './ViewOrderButton';

// Tiles (Admin Panel grouping, minus the internal "No KAM mapped"); each one also filters the list.
const TILES = { ALL: 'all', REPEAT: 'repeat', NO_NEW_DATE: 'noNewDate', NO_FIRST: 'noFirst' };
const BAND_BY_KEY = NO_NEW_DATE_AGE_BANDS.reduce((acc, b) => ({ ...acc, [b.key]: b }), {});
const isNoNewDateTile = (tile) => tile === TILES.NO_NEW_DATE || !!BAND_BY_KEY[tile];

// Oldest first, so the longest-pending appointments are on top.
const OVERDUE_SORT = { field: 'appointmentDeliveryData.appointmentDeliveryEndTime', direction: 'ASC' };
const NO_NEW_DATE_SORT = { field: 'appointmentDeliveryHistory.appointmentDeliveryStartTime', direction: 'ASC' };
const NO_FIRST_SORT = { field: 'awbRegisteredDate', direction: 'ASC' };

const DAY_MS = 86400000;
const daysSince = (date, now) => {
  if (!date) return null;
  // parseDate reads the server dates the way formatDate (and the ABD listing) shows them.
  const d = parseDate(date);
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const b = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((a - b) / DAY_MS);
};
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

const fmtSlot = (start, end) => {
  if (!start) return '-';
  const date = formatDate(start, 'dd MMM yyyy');
  return end ? `${date}, ${formatDate(start, 'h:mm a')} – ${formatDate(end, 'h:mm a')}` : `${date}, ${formatDate(start, 'h:mm a')}`;
};

// Same appointment number rule as the ABD listing: misses so far plus the current one.
const appointmentNo = (row) => (row?.appointmentDeliveryHistoryCount ?? row?.appointmentDeliveryHistory?.length ?? 0) + 1;

const Badge = ({ color, children }) => <Box component='span' sx={{ ...badgeSx(color), whiteSpace: 'nowrap' }}>{children}</Box>;

const awbColumn = {
  field: 'awb', headerName: 'AWB / Ref', width: '17%',
  renderCell: ({ row }) => (
    <Box>
      <AwbLinkCell awb={row?.awb_number} fallback='-' sx={{ ...awbTextSx, whiteSpace: 'nowrap' }} />
      {row?.reference && <Typography sx={{ ...tertiaryTextSx, wordBreak: 'break-word' }}>Ref: {row.reference}</Typography>}
      <Typography sx={tertiaryTextSx}>{row?.courierDetail?.parent || ''}</Typography>
    </Box>
  ),
};

const destinationColumn = {
  field: 'destination', headerName: 'Destination', width: '13%',
  renderCell: ({ row }) => (
    <Box>
      <Typography sx={secondaryTextSx}>{row?.deliveryDetails?.to_city || row?.delivery_details?.to_city || '-'}</Typography>
      <Typography sx={tertiaryTextSx}>{row?.deliveryDetails?.to_pincode || row?.delivery_details?.to_pincode || ''}</Typography>
    </Box>
  ),
};

const shipmentStatus = (row) => (
  <Chip size='small' label={String(row?.orderStatus || '-').replace(/_/g, ' ')} sx={getStatusChipSx(row?.orderStatus)} />
);

const buildOverdueColumns = (now) => [
  awbColumn,
  {
    field: 'appointment', headerName: 'Appointment', width: '19%',
    renderCell: ({ row }) => {
      const d = row?.appointmentDeliveryData || {};
      return (
        <Box>
          <Typography sx={primaryTextSx}>{fmtSlot(d.appointment_del_start, d.appointment_del_end)}</Typography>
          <Typography sx={tertiaryTextSx}>Appt #{appointmentNo(row)}</Typography>
        </Box>
      );
    },
  },
  {
    field: 'status', headerName: 'Appointment Status', width: '16%',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        <Badge color={ERROR}>Open · not closed</Badge>
        {shipmentStatus(row)}
      </Box>
    ),
  },
  {
    field: 'pending', headerName: 'Pending', width: '10%',
    renderCell: ({ row }) => {
      const days = daysSince(row?.appointmentDeliveryData?.appointment_del_end, now);
      if (days == null) return <Typography sx={secondaryTextSx}>-</Typography>;
      return (
        <Box>
          <Typography sx={{ ...primaryTextSx, color: ERROR }}>{days <= 0 ? 'Today' : plural(days, 'day')}</Typography>
          <Typography sx={tertiaryTextSx}>since slot end</Typography>
        </Box>
      );
    },
  },
  {
    field: 'reason', headerName: 'Delay Reason / Attribution', width: '19%',
    renderCell: () => (
      <Box>
        <Badge color={SUBTLE}>Not recorded yet</Badge>
        <Typography sx={{ ...tertiaryTextSx, mt: 0.5 }}>Prozo will record the reason when it closes this appointment</Typography>
      </Box>
    ),
  },
  destinationColumn,
  viewColumn,
];

// Same columns as the Admin Panel's "Missed, no new date" list (Last Missed Appt, Misses, Days Since Miss), plus the
// merchant extras: appointment status, the reason / attribution in merchant wording, destination and the view icon.
const buildNoNewDateColumns = (now, config) => [
  awbColumn,
  {
    field: 'lastMiss', headerName: 'Last Missed Appt', width: '16%',
    renderCell: ({ row }) => (
      <Box>
        <Typography sx={primaryTextSx}>{fmtSlot(lastMiss(row)?.appointment_del_start)}</Typography>
        <Typography sx={tertiaryTextSx}>Appt #{row?.appointmentDeliveryHistoryCount ?? '-'} · missed</Typography>
      </Box>
    ),
  },
  {
    field: 'status', headerName: 'Appointment Status', width: '14%',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        <Badge color={WARNING}>Missed · no new date</Badge>
        {shipmentStatus(row)}
      </Box>
    ),
  },
  {
    field: 'misses', headerName: 'Misses', width: '7%',
    renderCell: ({ row }) => <Typography sx={{ ...primaryTextSx, fontWeight: 700 }}>{row?.appointmentDeliveryHistoryCount ?? 0}×</Typography>,
  },
  {
    field: 'daysSince', headerName: 'Days Since Miss', width: '10%',
    renderCell: ({ row }) => {
      const days = daysSince(lastMiss(row)?.appointment_del_start, now);
      if (days == null) return <Typography sx={secondaryTextSx}>-</Typography>;
      return (
        <Box>
          <Typography sx={{ ...primaryTextSx, fontWeight: 800, fontSize: '15px', color: ageBandOf(days)?.color || TOKEN_COLOR.text }}>{days}</Typography>
          <Typography sx={tertiaryTextSx}>days since miss</Typography>
        </Box>
      );
    },
  },
  {
    field: 'reason', headerName: 'Delay Reason / Attribution', width: '21%',
    renderCell: ({ row }) => {
      const miss = lastMiss(row);
      const owner = MISS_ATTRIBUTION[miss?.appointment_closure_category];
      const reason = getReasonLabel(config, miss?.appointment_closure_reason);
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          {owner ? <Badge color={owner.color}>{owner.label}</Badge> : <Badge color={SUBTLE}>Not recorded</Badge>}
          {reason && <Typography sx={secondaryTextSx}>{reason}</Typography>}
          {miss?.appointment_closure_remarks && (
            <Typography sx={{ ...tertiaryTextSx, wordBreak: 'break-word' }}>“{miss.appointment_closure_remarks}”</Typography>
          )}
        </Box>
      );
    },
  },
  { ...destinationColumn, width: '9%' },
  viewColumn,
];

const buildNoFirstColumns = (now) => [
  { ...awbColumn, width: '22%' },
  {
    field: 'registered', headerName: 'AWB Registered', width: '18%',
    renderCell: ({ row }) => {
      const days = daysSince(fromUtc(row?.awbRegisteredDate), now);
      return (
        <Box>
          <Typography sx={primaryTextSx}>{row?.awbRegisteredDate ? formatDate(fromUtc(row.awbRegisteredDate), 'dd MMM yyyy') : '-'}</Typography>
          {days != null && <Typography sx={tertiaryTextSx}>{days <= 0 ? 'Today' : `${plural(days, 'day')} ago`}</Typography>}
        </Box>
      );
    },
  },
  {
    field: 'status', headerName: 'Appointment Status', width: '20%',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        <Badge color={SUBTLE}>Waiting for 1st appointment</Badge>
        {shipmentStatus(row)}
      </Box>
    ),
  },
  {
    field: 'edd', headerName: 'EDD', width: '15%',
    renderCell: ({ row }) => <Typography sx={primaryTextSx}>{row?.edd ? formatDate(fromUtc(row.edd), 'dd MMM yyyy') : '-'}</Typography>,
  },
  { ...destinationColumn, width: '19%' },
  viewColumn,
];

/**
 * Overdue ABD appointments for the logged-in merchant, read-only. Mirrors the Admin Panel's Overdue Appointments:
 * "Slot passed, not closed", "Missed, no new date" (aged by the latest miss) and "No first appointment".
 * No Mark as Missed / Book next appt; the eye icon only opens the order details.
 */
export default function OverdueAppointments() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(50);
  const [reload, setReload] = useState(0);
  const [now, setNow] = useState(() => new Date());
  // The landing page links here with ?view=noNewDate.
  const [tile, setTile] = useState(() => {
    const view = searchParams.get('view');
    return Object.values(TILES).includes(view) || BAND_BY_KEY[view] ? view : TILES.ALL;
  });
  const { config } = useAppointmentConfig();

  const overdueFilter = useMemo(() => buildOverdueFilter(now), [now]);
  const filterFor = (t) => {
    if (t === TILES.ALL) return overdueFilter;
    if (t === TILES.NO_FIRST) return noFirstAppointmentFilter(now);
    if (t === TILES.REPEAT) return [...overdueFilter, { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 }];
    const band = BAND_BY_KEY[t];
    return [...NO_NEW_DATE_FILTER, ...(band ? ageBandFilter(band, now) : [])];
  };
  const noNewDateView = isNoNewDateTile(tile);
  const noFirstView = tile === TILES.NO_FIRST;
  const listFilter = filterFor(tile);

  const list = useAppointmentSearch({
    filters, extraAndFilter: listFilter, page, sizePerPage, reload,
    sort: noFirstView ? NO_FIRST_SORT : noNewDateView ? NO_NEW_DATE_SORT : OVERDUE_SORT,
  });
  // Tile counts: same filters, one row each (only the count is used).
  const countOf = (t) => ({ filters, extraAndFilter: filterFor(t), page: 0, sizePerPage: 1, reload });
  const allCount = useAppointmentSearch(countOf(TILES.ALL));
  const repeatCount = useAppointmentSearch(countOf(TILES.REPEAT));
  const noNewDateCount = useAppointmentSearch(countOf(TILES.NO_NEW_DATE));
  const noFirstCount = useAppointmentSearch(countOf(TILES.NO_FIRST));
  // One count per age band (fixed list, so the hook order never changes).
  const bandCounts = NO_NEW_DATE_AGE_BANDS.map((band) => ({ band, query: useAppointmentSearch(countOf(band.key)) })); // eslint-disable-line react-hooks/rules-of-hooks

  const value = (q) => (q.isFetching ? undefined : q.data?.count ?? 0);
  const noNewDateTotal = noNewDateCount.data?.count ?? 0;
  const pickTile = (t) => { setTile(t); setPage(0); };
  const refresh = () => { setNow(new Date()); setReload((r) => r + 1); };

  // While a tile's list loads the previous tile's rows are still cached; the two lists have different
  // columns, so show nothing until the right rows arrive.
  const rows = !list.isFetching && list.data?.data?.length ? list.data.data : [];
  const total = list.isFetching ? 0 : list.data?.count ?? 0;
  const columns = noFirstView ? buildNoFirstColumns(now) : noNewDateView ? buildNoNewDateColumns(now, config) : buildOverdueColumns(now);
  const listName = noFirstView ? 'No first appointment' : noNewDateView ? 'Missed, no new date' : 'Slot passed, not closed';

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='ABD appointments past their slot, missed without a new date, or not booked yet'>Overdue Appointments</PageTitle>

      <SectionTitle title='Open appointments' hint={`slot passed and never marked as missed, or no first appointment yet (AWB registered in the last ${NO_FIRST_APPOINTMENT_DAYS} days)`} />
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard color='red' label='Overdue, Not Closed' loading={allCount.isFetching} value={value(allCount)} sub='All overdue · click to show all'
            icon={<ReportProblemIcon />} selected={tile === TILES.ALL} onClick={() => pickTile(TILES.ALL)}
            info='Open appointments from an earlier day that were never marked as missed. Delivered, RTO and cancelled shipments are not listed.' />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard color='amber' label='Repeat Misses' loading={repeatCount.isFetching} value={value(repeatCount)} sub='Missed at least once before'
            icon={<RepeatIcon />} selected={tile === TILES.REPEAT} onClick={() => pickTile(TILES.REPEAT)}
            info='Overdue appointments on shipments that already have at least one missed appointment (Appt #2 or later).' />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard color='grey' label='Waiting for 1st Appointment' loading={noFirstCount.isFetching} value={value(noFirstCount)} sub='Appointment date not shared yet'
            icon={<HourglassEmptyIcon />} selected={tile === TILES.NO_FIRST} onClick={() => pickTile(TILES.NO_FIRST)}
            info={`ABD shipments with no appointment booked and never missed, AWB registered in the last ${NO_FIRST_APPOINTMENT_DAYS} days. Delivered, RTO and cancelled shipments are not listed.`} />
        </Grid>
      </Grid>

      <SectionTitle title='Missed, no new date' hint='marked as missed with a reason, no fresh appointment booked · age counted from the missed appointment date' />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard color='red' label='No New Date · All' loading={noNewDateCount.isFetching} value={value(noNewDateCount)} sub='Click to show all'
            icon={<AccessTimeIcon />} selected={tile === TILES.NO_NEW_DATE} onClick={() => pickTile(TILES.NO_NEW_DATE)}
            info='Shipments marked as missed with a reason whose next appointment is not booked yet. Delivered, RTO and cancelled shipments are not listed.' />
        </Grid>
        {bandCounts.map(({ band, query }, i) => {
          const n = query.data?.count ?? 0;
          return (
            <Grid item xs={12} sm={6} md={2.4} key={band.key}>
              <KpiCard color={band.kpiColor} label={band.label} loading={query.isFetching} value={value(query)} valueColor={band.color}
                sub={<><PctChip value={n} total={noNewDateTotal} color={band.color} bg={band.bg} />{band.hint}</>}
                icon={i >= 2 ? <WarningAmberIcon /> : <AccessTimeIcon />}
                selected={tile === band.key} onClick={() => pickTile(band.key)}
                info={`Missed, no new date, last missed appointment ${band.label} ago.`} />
            </Grid>
          );
        })}
      </Grid>
      <ShareBar total={noNewDateTotal}
        segments={bandCounts.map(({ band, query }) => ({ key: band.key, label: band.label, color: band.color, value: query.data?.count ?? 0 }))} />

      <PageBanner variant='info' icon={<InfoOutlinedIcon />} title='For your information'>
        <strong>Slot passed, not closed:</strong> the appointment date has passed and Prozo has not updated it yet
        (an appointment on 12 Oct shows here from 13 Oct). <strong>Missed, no new date:</strong> the appointment was
        missed, the reason and attribution are recorded, and the next appointment is not booked yet. Prozo's PTL team
        follows up on both. <strong>No first appointment:</strong> the shipment needs an appointment and none is
        booked yet.
      </PageBanner>

      <FilterBar
        show={['courier', 'awb']}
        initial={{ fromDate: null, toDate: null }}
        onApply={(f) => { setFilters({ ...f, fromDate: null, toDate: null }); setPage(0); refresh(); }}
        onReset={() => { setFilters({}); setPage(0); refresh(); }}
      />

      <Box sx={{ ...btnRowSx, mb: 1.5 }}>
        <Typography sx={tertiaryTextSx}>
          {listName} · {plural(total, 'shipment')} · oldest first · checked at {formatDate(now, 'h:mm a, dd MMM')}
        </Typography>
        <Button size='small' startIcon={<RefreshIcon />} onClick={refresh} sx={{ textTransform: 'none' }}>Refresh</Button>
        <Box sx={{ ml: 'auto' }}>
          <ExportButton label='Export' filters={filters} extraAndFilter={listFilter}
            fileName={noFirstView ? 'ABD_No_First_Appointment' : noNewDateView ? 'ABD_Missed_No_New_Date' : 'ABD_Overdue_Appointments'} />
        </Box>
      </Box>

      {list.error ? (
        <Alert severity='error'>{errorText(list.error, 'Could not load the appointments. Please refresh.')}</Alert>
      ) : !list.isFetching && total === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2 }}>
          <InboxIcon sx={{ fontSize: 28, color: '#bdbdbd' }} />
          <Typography sx={{ color: '#666', mb: 1 }}>
            {noFirstView ? 'No shipments waiting for a first appointment for the selected filters.'
              : noNewDateView ? 'No missed shipments waiting for a new date for the selected filters.' : 'No overdue appointments for the selected filters.'}
          </Typography>
          <Button size='small' component={Link} to='/appointments/all'>Go to All Appointments</Button>
        </Box>
      ) : (
        <ExpandableAppointmentsTable
          columns={columns}
          rows={rows}
          page={page}
          sizePerPage={sizePerPage}
          setPage={setPage}
          setSizePerPage={setSizePerPage}
          rowCount={total}
          getRowId={(row) => row.id}
          loading={list.isFetching}
        />
      )}
    </Box>
  );
}
